/**
 * The product-prefixed URLs the app used before Ad Management.
 *
 * Bookmarks, Notion runbooks, Intercom macros and years of Slack links all point at `/adm/…` and
 * `/pbs/…`, so every one of them is rewritten rather than left to the catch-all. These rules are
 * permanent: there is no follow-up that removes them.
 *
 * Almost every page moved verbatim under a product segment, so the rewrite is a prefix substitution
 * and route params ride along untouched — which is the whole reason this is a string function and not
 * a hand-written `Redirect` per leaf. There were 103 of those, each one a chance to mistype a param
 * name that no compiler would catch.
 */

type LegacyRule = {
  from: string
  to: string
  /** Matches the path itself and nothing below it. Used where a page did *not* move verbatim. */
  exact?: boolean
}

/**
 * Ordered — the first match wins, which is what will let a later iteration send a page that has been
 * unified somewhere its own children do not follow (`/adm/adunits` to a shared tab, while
 * `/adm/adunits/edit/:id` stays the One editor). Today every page still moves verbatim under its
 * product, so the two blanket prefixes carry all of it and `exact` has no user yet.
 */
export const LEGACY_ROUTE_RULES: LegacyRule[] = [
  // The ad-unit lists merged into one page, and their children did not follow them: the editors and
  // the CSV import are still per-product, so they fall through to the blanket prefixes below.
  { from: '/adm/adunits', to: '/monetization/inventory/adunits/web', exact: true },
  { from: '/pbs/adunits', to: '/monetization/inventory/adunits/server-side', exact: true },
  // `/pbs/adunits/web` listed *server-side* ad units — the path said web, the env said s2s. The new
  // Web tab is One's inventory, so this is the one redirect whose meaning moves, not just its spelling.
  { from: '/pbs/adunits/web', to: '/monetization/inventory/adunits/server-side', exact: true },
  { from: '/pbs/adunits/inapp', to: '/monetization/inventory/adunits/inapp', exact: true },
  { from: '/pbs/adunits/amp', to: '/monetization/inventory/adunits/amp', exact: true },
  { from: '/adm/sites', to: '/monetization/inventory/medias', exact: true },
  { from: '/pbs/sites', to: '/monetization/inventory/medias', exact: true },
  // The two deployment centers merged into one. `exact` is belt-and-braces here — neither page ever had
  // children — but it keeps these rules the same shape as every other unified page above.
  { from: '/adm/deployment-center', to: '/monetization/inventory/deployment-center', exact: true },
  { from: '/pbs/deployment-center', to: '/monetization/inventory/deployment-center', exact: true },
  // The catalog and the seat list merged too. `exact` matters twice over here: the seat *page* stays
  // per-product, so `/adm/integrations/my-bidder-seats/:id` must fall through to the blanket prefix
  // and land on One's seat page rather than on the shared list.
  { from: '/adm/integrations/catalog', to: '/monetization/inventory/catalog', exact: true },
  { from: '/pbs/integrations/catalog', to: '/monetization/inventory/catalog', exact: true },
  { from: '/adm/integrations/my-bidder-seats', to: '/monetization/inventory/my-bidder-seats', exact: true },
  { from: '/pbs/integrations/my-bidder-seats', to: '/monetization/inventory/my-bidder-seats', exact: true },
  // Not `exact`: the rule editors moved with the list, so `/adm/floor-price/edit/:id` follows it.
  { from: '/adm/floor-price', to: '/monetization/inventory/floor-price' },
  { from: '/pbs/floor-price', to: '/monetization/inventory/floor-price' },
  { from: '/adm', to: '/monetization/one' },
  { from: '/pbs', to: '/monetization/node' },
]

/**
 * Where an old product path lives now, or `null` when it was never one. `null` is what keeps `/admin`
 * from being swallowed by the `/adm` rule.
 */
export const rewriteLegacyPath = (pathname: string): string | null => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  for (const { from, to, exact } of LEGACY_ROUTE_RULES) {
    if (path === from) {
      return to
    }
    if (!exact && path.startsWith(`${from}/`)) {
      return `${to}${path.slice(from.length)}`
    }
  }
  return null
}
